import axios, { AxiosError, ResponseType } from "axios";
import { api_config } from "@/data/config/api-config";
import { auth } from "@/data/config/firebase-config";
import { ErrorData, RequestType } from "@/data/types";
import { constructRequestHeaders } from "./headers";

// Add a new type for the progress callback
export type ProgressCallback = (progress: number) => void;

// Request configuration interface
export interface RequestConfig<T = any> {
  path: string;
  type: RequestType;
  params?: any;
  body?: T;
  token?: string;
  responseType?: ResponseType;
  contentType?: string;
  onProgress?: ProgressCallback;
  abortController?: AbortController;
}

/**
 * A FormData body has to carry its own multipart boundary, which only the
 * browser can generate. Dropping the header lets axios fill it in; keeping the
 * hardcoded `multipart/form-data` would leave the server unable to parse it.
 */
async function buildHeaders(token: string | undefined, contentType: string | undefined, body: unknown) {
  const headers = constructRequestHeaders(token, contentType);

  if (typeof FormData !== "undefined" && body instanceof FormData) {
    delete headers["Content-Type"];
  }

  return headers;
}

/**
 * Make HTTP requests on server, request functions should go in the API folder
 *
 * @param config Request configuration object
 * @returns {Promise<AxiosResponse<T>>}
 */
export function Request<T = any>(config: RequestConfig<T>) {
  const {
    path,
    type,
    params,
    body,
    token,
    responseType,
    contentType,
    onProgress,
    abortController,
  } = config;

  const baseUrl = api_config.baseUrl;
  const user = auth?.currentUser;

  return new Promise(async (resolve, reject) => {
    axios({
      method: type,
      url: `${baseUrl}/${path}`,
      params: params,
      data: body,
      headers: await buildHeaders(
        token || (await user?.getIdToken()),
        contentType,
        body
      ),
      responseType: responseType,
      signal: abortController?.signal,
      onDownloadProgress: onProgress
        ? (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) /
                (progressEvent.total || progressEvent.loaded)
            );
            onProgress(percentCompleted);
          }
        : undefined,
      onUploadProgress:
        type === "POST" || type === "PATCH"
          ? onProgress
            ? (progressEvent) => {
                const percentCompleted = Math.round(
                  (progressEvent.loaded * 100) /
                    (progressEvent.total || progressEvent.loaded)
                );
                onProgress(percentCompleted);
              }
            : undefined
          : undefined,
    })
      .then((response) => {
        if (response.status >= 200 && response.status < 300) {
          resolve(response.data);
        }
      })
      .catch((error: AxiosError<ErrorData>) => {
        reject(JSON.stringify(error.response?.data));
      });
  });
}
